import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Block,
  CalendarToday,
} from '@mui/icons-material';
import { DateRange, DateRangePicker, DateRangePickerDayProps } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { MuiTextFieldProps } from '@mui/x-date-pickers/internals';
import axios from 'axios';
import ptBR from 'date-fns/locale/pt';
import moment from 'moment';

import { UseFormik } from '@/utils/Formik/types';
import { ErrorMessage } from '@/utils/Messages';

import { getAvailabilityDates } from '@/services/Reservation/request';

import { useViewMode } from '@/context/ViewMode/ViewMode';

import {
  AdornmentContainer,
  Container,
  ContainerTextField,
  Content,
  CustomDay,
  DateRangePickerDay,
  IconContainer,
  Label,
  RightArrowIndicator,
  StyledLocalizationProvider,
  StyledTextField,
  UnnavailableDayMarker,
} from './styles';

interface DatePickerProps {
  label?: string;
  id1: string;
  id2: string;
  startText?: string;
  endText?: string;
  labelTextField1?: string;
  labelTextField2?: string;
  hiddenIcon?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  allowSameDateSelection?: boolean;
  showDaysOutsideCurrentMonth?: boolean;
  disableCloseOnSelect?: boolean;
  disableAutoMonthSwitching?: boolean;
  className?: string;
  formik?: UseFormik<any>;
  minDate?: Date;
  helperText?: string;
  hidden?: boolean;
  calendars?: 1 | 2;
  hasInitialDates?: boolean;
  showingLabel?: boolean;
  propertyId?: number;
  calendarHasMarkers?: boolean;
  disableInput?: boolean;
  dataCyTextField1?: string;
  dataCyTextField2?: string;
  ref?: any;
  labelVariant?: 'primary' | 'secondary';
  inputVariant?: 'primary' | 'secondary';
  onChange?: any;
}

const IconCalendar = () => (
  <IconContainer>
    <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 1H9.5V0.5C9.5 0.367392 9.44732 0.240215 9.35355 0.146447C9.25979 0.0526784 9.13261 0 9 0C8.86739 0 8.74021 0.0526784 8.64645 0.146447C8.55268 0.240215 8.5 0.367392 8.5 0.5V1H3.5V0.5C3.5 0.367392 3.44732 0.240215 3.35355 0.146447C3.25979 0.0526784 3.13261 0 3 0C2.86739 0 2.74021 0.0526784 2.64645 0.146447C2.55268 0.240215 2.5 0.367392 2.5 0.5V1H1C0.734784 1 0.48043 1.10536 0.292893 1.29289C0.105357 1.48043 0 1.73478 0 2V12C0 12.2652 0.105357 12.5196 0.292893 12.7071C0.48043 12.8946 0.734784 13 1 13H11C11.2652 13 11.5196 12.8946 11.7071 12.7071C11.8946 12.5196 12 12.2652 12 12V2C12 1.73478 11.8946 1.48043 11.7071 1.29289C11.5196 1.10536 11.2652 1 11 1ZM2.5 2V2.5C2.5 2.63261 2.55268 2.75979 2.64645 2.85355C2.74021 2.94732 2.86739 3 3 3C3.13261 3 3.25979 2.94732 3.35355 2.85355C3.44732 2.75979 3.5 2.63261 3.5 2.5V2H8.5V2.5C8.5 2.63261 8.55268 2.75979 8.64645 2.85355C8.74021 2.94732 8.86739 3 9 3C9.13261 3 9.25979 2.94732 9.35355 2.85355C9.44732 2.75979 9.5 2.63261 9.5 2.5V2H11V4H1V2H2.5ZM11 12H1V5H11V12ZM6.75 7.25C6.75 7.39834 6.70601 7.54334 6.6236 7.66668C6.54119 7.79001 6.42406 7.88614 6.28701 7.94291C6.14997 7.99967 5.99917 8.01453 5.85368 7.98559C5.7082 7.95665 5.57456 7.88522 5.46967 7.78033C5.36478 7.67544 5.29335 7.5418 5.26441 7.39632C5.23547 7.25083 5.25032 7.10003 5.30709 6.96299C5.36386 6.82594 5.45999 6.70881 5.58332 6.6264C5.70666 6.54399 5.85166 6.5 6 6.5C6.19891 6.5 6.38968 6.57902 6.53033 6.71967C6.67098 6.86032 6.75 7.05109 6.75 7.25ZM9.5 7.25C9.5 7.39834 9.45601 7.54334 9.3736 7.66668C9.29119 7.79001 9.17406 7.88614 9.03701 7.94291C8.89997 7.99967 8.74917 8.01453 8.60368 7.98559C8.4582 7.95665 8.32456 7.88522 8.21967 7.78033C8.11478 7.67544 8.04335 7.5418 8.01441 7.39632C7.98547 7.25083 8.00033 7.10003 8.05709 6.96299C8.11386 6.82594 8.20999 6.70881 8.33332 6.6264C8.45666 6.54399 8.60166 6.5 8.75 6.5C8.94891 6.5 9.13968 6.57902 9.28033 6.71967C9.42098 6.86032 9.5 7.05109 9.5 7.25ZM4 9.75C4 9.89834 3.95601 10.0433 3.8736 10.1667C3.79119 10.29 3.67406 10.3861 3.53701 10.4429C3.39997 10.4997 3.24917 10.5145 3.10368 10.4856C2.9582 10.4566 2.82456 10.3852 2.71967 10.2803C2.61478 10.1754 2.54335 10.0418 2.51441 9.89632C2.48547 9.75083 2.50032 9.60003 2.55709 9.46299C2.61386 9.32594 2.70999 9.20881 2.83332 9.1264C2.95666 9.04399 3.10166 9 3.25 9C3.44891 9 3.63968 9.07902 3.78033 9.21967C3.92098 9.36032 4 9.55109 4 9.75ZM6.75 9.75C6.75 9.89834 6.70601 10.0433 6.6236 10.1667C6.54119 10.29 6.42406 10.3861 6.28701 10.4429C6.14997 10.4997 5.99917 10.5145 5.85368 10.4856C5.7082 10.4566 5.57456 10.3852 5.46967 10.2803C5.36478 10.1754 5.29335 10.0418 5.26441 9.89632C5.23547 9.75083 5.25032 9.60003 5.30709 9.46299C5.36386 9.32594 5.45999 9.20881 5.58332 9.1264C5.70666 9.04399 5.85166 9 6 9C6.19891 9 6.38968 9.07902 6.53033 9.21967C6.67098 9.36032 6.75 9.55109 6.75 9.75ZM9.5 9.75C9.5 9.89834 9.45601 10.0433 9.3736 10.1667C9.29119 10.29 9.17406 10.3861 9.03701 10.4429C8.89997 10.4997 8.74917 10.5145 8.60368 10.4856C8.4582 10.4566 8.32456 10.3852 8.21967 10.2803C8.11478 10.1754 8.04335 10.0418 8.01441 9.89632C7.98547 9.75083 8.00033 9.60003 8.05709 9.46299C8.11386 9.32594 8.20999 9.20881 8.33332 9.1264C8.45666 9.04399 8.60166 9 8.75 9C8.94891 9 9.13968 9.07902 9.28033 9.21967C9.42098 9.36032 9.5 9.55109 9.5 9.75Z"
        fill="#394760"
      />
    </svg>
  </IconContainer>
);

const DatePickerRange: React.FC<DatePickerProps> = ({
  label,
  id1,
  id2,
  startText = '',
  endText = '',
  labelTextField1,
  labelTextField2,
  hiddenIcon,
  disablePast = false,
  disableFuture = false,
  allowSameDateSelection,
  showDaysOutsideCurrentMonth = false,
  disableCloseOnSelect = false,
  disableAutoMonthSwitching = false,
  className = 'datepicker-range',
  minDate,
  formik,
  hidden = false,
  calendars = 1,
  hasInitialDates = false,
  showingLabel = true,
  propertyId,
  calendarHasMarkers = false,
  disableInput = false,
  dataCyTextField1 = 'datepicker1',
  dataCyTextField2 = 'datepicker2',
  ref = null,
  labelVariant = 'primary',
  inputVariant = 'primary',
  onChange,
}) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const location = useLocation();
  const { mode } = useViewMode();

  const [initialDate] = useState<DateRange<Date>>(
    hasInitialDates ? [formik?.values[id1], formik?.values[id2]] : [today, tomorrow],
  );

  const [selectedDate, setSelectedDate] = useState<DateRange<Date>>(initialDate);

  const [onOpen, setOnOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [calendarOption, setCalendarOption] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const [month, setMonth] = useState<number>(
    hasInitialDates && initialDate?.[0] ? moment(initialDate?.[0]).month() : moment().month(),
  );
  const [year, setYear] = useState<number>(
    hasInitialDates && initialDate?.[0] ? moment(initialDate?.[0]).year() : moment().year(),
  );
  const months: string[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

  const getDaysInMonth = (date: string) => {
    const numberDays: number = moment(date, 'YYYY-MM').daysInMonth();
    return numberDays;
  };
  const [actualDate, setActualDate] = useState<Date>(
    hasInitialDates && formik?.values[id1]
      ? moment(formik?.values[id1], 'YYYY-MM-DD').toDate()
      : new Date(),
  );
  const currentDate = `${year}-${months[month]}`;
  const [startDate, setStartDate] = useState<string>(`${currentDate}-01`);
  const [endDate, setEndDate] = useState<string>(`${currentDate}-${getDaysInMonth(currentDate)}`);

  useEffect(() => {
    let initial = `${moment(actualDate).format('YYYY-MM')}`;
    const final = `${initial}-${getDaysInMonth(initial)}`;
    initial = `${initial}-01`;
    setStartDate(initial);
    setEndDate(final);
  }, [actualDate]);

  useEffect(() => {
    setReload((prevReload) => !prevReload);
  }, [startDate, endDate]);

  const handleDateChange = (newDate: DateRange<Date>) => {
    formik?.setFieldTouched(id1, true, false);
    formik?.setFieldTouched(id2, true, false);

    const date1 = moment(newDate[0]);
    const date2 = moment(newDate[1]);
    const year1 = Number(date1.format('YYYY'));
    const year2 = Number(date2.format('YYYY'));

    if ((year1 < 1870 || Number.isNaN(year1)) && newDate[0] !== null) {
      if (formik?.errors[id1] !== ErrorMessage.invalidDate()) {
        formik?.setFieldError(id1, ErrorMessage.invalidDate());
      }
      return;
    }
    if ((year2 < 1870 || Number.isNaN(year2)) && newDate[1] !== null) {
      if (formik?.errors[id2] !== ErrorMessage.invalidDate()) {
        formik?.setFieldError(id2, ErrorMessage.invalidDate());
      }
      return;
    }
    const minDateMoment = moment(minDate);
    if (minDate && Number(date1.format('YYYYMMDD')) < Number(minDateMoment.format('YYYYMMDD'))) {
      if (formik?.errors[id1] !== ErrorMessage.minDate(minDate)) {
        formik?.setFieldError(id1, ErrorMessage.minDate(minDate));
      }
      return;
    }
    if (minDate && Number(date2.format('YYYYMMDD')) < Number(minDateMoment.format('YYYYMMDD'))) {
      if (formik?.errors[id2] !== ErrorMessage.minDate(minDate)) {
        formik?.setFieldError(id2, ErrorMessage.minDate(minDate));
      }
      return;
    }
    if (!calendarHasMarkers) {
      setSelectedDate(newDate);
      formik?.setFieldValue(id1, newDate[0]);
      formik?.setFieldValue(id2, newDate[1]);
    } else if (calendarOption === 1) {
      if (newDate[0] !== null && newDate[1] !== null) {
        setSelectedDate([newDate[0], newDate[1]]);
        formik?.setFieldValue(id1, newDate[0]);
        formik?.setFieldValue(id2, newDate[1]);
      } else {
        setSelectedDate(newDate[1] !== null ? [newDate[1], newDate[0]] : [newDate[0], newDate[1]]);
        formik?.setFieldValue(id1, newDate[1] !== null ? newDate[1] : newDate[0]);
      }
    } else if (calendarOption === 2) {
      if (newDate[0] !== null && newDate[1] !== null) {
        setSelectedDate([newDate[0], newDate[1]]);
        formik?.setFieldValue(id1, newDate[0]);
        formik?.setFieldValue(id2, newDate[1]);
      } else {
        setSelectedDate([formik?.values[id1], newDate[0]]);
        formik?.setFieldValue(id2, newDate[0]);
      }
    }
    if (onChange) onChange();
  };

  useEffect(() => {
    let isMounted = true;
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    setLoading(true);
    try {
      if (onOpen && propertyId && calendarHasMarkers) {
        if (`${location.pathname}`.startsWith('/multicalendar')) {
          getAvailabilityDates({
            propertyId,
            startDate,
            endDate,
            source,
            contextUsed: 'multicalendar',
          }).then((response) => {
            if (isMounted && response?.results) {
              const unavailables: string[] = Object.keys(response.results).filter(
                (date) => !response.results[date],
              );

              if (unavailables.length > 0) {
                const unavailablesNext: string[] = [];
                unavailables.forEach((dateStr: string) => {
                  const yyyy = dateStr.split('-')[0];
                  const mm = dateStr.split('-')[1];
                  const dd =
                    Number(dateStr.split('-')[2]) + 1 < 10
                      ? `0${Number(dateStr.split('-')[2]) + 1}`
                      : Number(dateStr.split('-')[2]) + 1;

                  const newDate = `${yyyy}-${mm}-${dd}`;
                  unavailablesNext.push(newDate);
                });
                setUnavailableDates(calendarOption === 1 ? unavailables : unavailablesNext);
              }
            }
          });
        } else {
          getAvailabilityDates({
            propertyId,
            startDate,
            endDate,
            source,
            contextUsed: 'owner_calendar',
            config: { mode },
          }).then((response) => {
            if (isMounted && response?.results) {
              const unavailables: string[] = Object.keys(response.results).filter(
                (date) => !response.results[date],
              );

              if (unavailables.length > 0) {
                const unavailablesNext: string[] = [];
                unavailables.forEach((dateStr: string) => {
                  const yyyy = dateStr.split('-')[0];
                  const mm = dateStr.split('-')[1];
                  const dd =
                    Number(dateStr.split('-')[2]) + 1 < 10
                      ? `0${Number(dateStr.split('-')[2]) + 1}`
                      : Number(dateStr.split('-')[2]) + 1;

                  const newDate = `${yyyy}-${mm}-${dd}`;
                  unavailablesNext.push(newDate);
                });
                setUnavailableDates(calendarOption === 1 ? unavailables : unavailablesNext);
              }
            }
          });
        }
      }

      setTimeout(() => {
        setLoading(false);
      }, 3500);
    } catch (err) {
      setLoading(false);
    }

    return () => {
      source.cancel();
      isMounted = false;
    };
  }, [onOpen, reload]);

  useEffect(() => {
    if (onOpen && calendarOption === 1) {
      formik?.setFieldValue(id1, initialDate[0]);
      formik?.setFieldTouched(id1, true, false);
      formik?.setFieldValue(id2, initialDate[1]);
      formik?.setFieldTouched(id2, true, false);
    }
  }, [onOpen, calendarOption]);

  useEffect(() => {
    if (formik?.values[id1] === null && formik?.values[id2] === null) {
      setSelectedDate([null, null]);
    }
  }, [formik?.values[id1], formik?.values[id2]]);

  const ArrowLeft = () => <ArrowBackIosIcon />;
  const ArrowRight = () => <ArrowForwardIosIcon />;

  const handleOpenCalendar = (inputClicked: number) => {
    if (inputClicked === 1) {
      setMonth(hasInitialDates ? moment(formik?.values[id1]).month() : moment().month());
      setYear(hasInitialDates ? moment(formik?.values[id1]).year() : moment().year());

      setActualDate(
        hasInitialDates && formik?.values[id1]
          ? moment(formik?.values[id1], 'YYYY-MM-DD').toDate()
          : new Date(),
      );
      localStorage.removeItem('firstBlock');
    }
    setCalendarOption(inputClicked);
    setOnOpen(!(inputClicked === 2 && selectedDate[0] === null));
  };

  const handleCloseCalendar = () => {
    setCalendarOption(1);
    setOnOpen(false);
  };

  const findFirstBlockingFromDateOrigin = useCallback(
    (dateOrigin: string) => {
      const firstOcurrence = unavailableDates.find(
        (dateStr) => new Date(dateStr) > new Date(dateOrigin),
      );

      const checkFirstBlock = localStorage.getItem('firstBlock');
      if (firstOcurrence && !checkFirstBlock) {
        localStorage.setItem('firstBlock', firstOcurrence);
      }
    },
    [unavailableDates],
  );

  const renderWeekPickerDay = useMemo(
    () => (date: Date, dateRangePickerDayProps: DateRangePickerDayProps<Date>) => {
      const day = date.getDate();
      const dateMoment = moment(date).format('YYYY-MM-DD');
      let isUnavailable = unavailableDates.includes(dateMoment);

      if (calendarOption === 2 && calendarHasMarkers) {
        const dateOrigin = moment(formik?.values[id1]).format('YYYY-MM-DD');
        findFirstBlockingFromDateOrigin(dateOrigin);
        const firstBlock = localStorage.getItem('firstBlock');

        if (firstBlock) {
          isUnavailable =
            unavailableDates.includes(dateMoment) ||
            new Date(dateMoment) <= new Date(dateOrigin) ||
            new Date(dateMoment) > new Date(firstBlock);
        } else {
          isUnavailable =
            unavailableDates.includes(dateMoment) || new Date(dateMoment) <= new Date(dateOrigin);
        }
      }

      if (isUnavailable && calendarHasMarkers) {
        return (
          <CustomDay key={dateMoment}>
            <UnnavailableDayMarker loading={loading} />
            <span>{day}</span>
          </CustomDay>
        );
      }

      if (!isUnavailable && calendarHasMarkers) {
        return (
          <DateRangePickerDay
            data-cy={`pickerdaymarker_${dateMoment}`}
            key={dateMoment}
            onClick={() => handleCloseCalendar()}
            {...dateRangePickerDayProps}
          />
        );
      }

      return (
        <DateRangePickerDay
          data-cy={`pickerday_${dateMoment}`}
          key={dateMoment}
          {...dateRangePickerDayProps}
        />
      );
    },
    [loading, calendarOption, unavailableDates],
  );

  type InputProps = {
    startProps: MuiTextFieldProps;
    endProps: MuiTextFieldProps;
  };

  useEffect(() => {
    if (formik?.values[id1] === null && formik?.values[id2] === null) return;

    setSelectedDate([formik?.values[id1], formik?.values[id2]]);
  }, [formik]);

  const InputPrimary = ({ startProps, endProps }: InputProps) => (
    <Content>
      <ContainerTextField onClick={() => handleOpenCalendar(1)} labelVariant={labelVariant}>
        {showingLabel && <Label>De</Label>}

        <div>
          {labelTextField1 && <Label>{labelTextField1}</Label>}
          <StyledTextField
            data-cy={dataCyTextField1}
            disabled={disableInput}
            margin="none"
            variant="outlined"
            size="small"
            helperText={null}
            value={selectedDate[0] === null ? '' : moment(selectedDate[0]).format('DD/MM/YYYY')}
            InputProps={{
              ...startProps.InputProps,
              endAdornment: (
                <AdornmentContainer>
                  <CalendarToday />
                </AdornmentContainer>
              ),
            }}
          />
        </div>
      </ContainerTextField>

      {!hiddenIcon && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showingLabel ? <Label>Até</Label> : <RightArrowIndicator size={30} />}
        </div>
      )}

      <ContainerTextField disabled={selectedDate[0] === null} labelVariant={labelVariant}>
        <div>
          {labelTextField2 && <Label>{labelTextField2}</Label>}
          <StyledTextField
            data-cy={dataCyTextField2}
            disabled={disableInput}
            margin="none"
            variant="outlined"
            size="small"
            helperText={null}
            value={selectedDate[1] === null ? '' : moment(selectedDate[1]).format('DD/MM/YYYY')}
            onClick={() => handleOpenCalendar(2)}
            InputProps={{
              ...endProps.InputProps,
              endAdornment: (
                <AdornmentContainer disabled={selectedDate[0] === null}>
                  {selectedDate[0] === null ? <Block /> : <CalendarToday />}
                </AdornmentContainer>
              ),
            }}
          />
        </div>
      </ContainerTextField>
    </Content>
  );

  const InputSecondary = ({ startProps, endProps }: InputProps) => (
    <Content>
      <ContainerTextField onClick={() => handleOpenCalendar(1)} labelVariant={labelVariant}>
        <StyledTextField
          data-cy={dataCyTextField1}
          disabled={disableInput}
          margin="none"
          variant="outlined"
          size="small"
          helperText={null}
          value={`${selectedDate[0] === null ? '' : moment(selectedDate[0]).format('DD/MM/YYYY')} até ${selectedDate[1] === null ? '' : moment(selectedDate[1]).format('DD/MM/YYYY')}`}
          InputProps={{
            ...startProps.InputProps,
            ...endProps.InputProps,
            startAdornment: (
              <AdornmentContainer>
                <IconCalendar />
              </AdornmentContainer>
            ),
          }}
        />
      </ContainerTextField>
    </Content>
  );

  return (
    <Container hidden={hidden}>
      <StyledLocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
        {label && <label htmlFor={label}>{label}</label>}
        <DateRangePicker
          calendars={calendars}
          open={onOpen}
          onClose={() => handleCloseCalendar()}
          value={selectedDate}
          startText={startText}
          endText={endText}
          okText="Confirmar"
          showToolbar={false}
          cancelText="Cancelar"
          toolbarTitle=""
          onChange={(date) => handleDateChange(date)}
          onMonthChange={(date) => setActualDate(date)}
          disablePast={disablePast}
          disableFuture={disableFuture}
          allowSameDateSelection={allowSameDateSelection}
          showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
          disableCloseOnSelect={disableCloseOnSelect}
          disableAutoMonthSwitching={disableAutoMonthSwitching}
          desktopModeMediaQuery="@media (min-width: 720px)"
          className={className}
          ref={ref}
          renderDay={renderWeekPickerDay}
          components={{
            LeftArrowIcon: ArrowLeft,
            RightArrowIcon: ArrowRight,
          }}
          renderInput={(startProps, endProps) =>
            inputVariant === 'primary' ? (
              <InputPrimary startProps={startProps} endProps={endProps} />
            ) : (
              <InputSecondary startProps={startProps} endProps={endProps} />
            )
          }
        />
      </StyledLocalizationProvider>
    </Container>
  );
};

export default DatePickerRange;
